import { createSelector } from '@reduxjs/toolkit';
import copy from 'clipboard-copy';

import { notify } from 'src/clients/ApiService';
import { useFeatureFlags } from 'src/context/flagsContext';
import { useDrawerWidth } from 'src/features/drawer/useDrawerWidth';
import { useCustomDomains } from 'src/hooks/useCustomDomains';
import { useAppDispatch, useAppSelector } from 'src/hooks/useStore';
import { GetDefaultCurrency } from 'src/legacy/components/Formatters';
import Layout, { PageShell } from 'src/legacy/components/Layout';
import { LoadingWrapper } from 'src/legacy/components/Loading';
import { PageContent } from 'src/legacy/components/UI';
import { ProductStoreForm } from 'src/routes/billing/store/components/ProductStoreForm';
import { ProductStoreSchema } from 'src/routes/billing/store/schema';
import { useRouteAccess } from 'src/routes/routeAccess';
import {
  useGetPricesByPortalQuery,
  useGetProductsQuery,
} from 'src/services/api/productsApi';
import {
  useCreateProductStoreMutation,
  useGetProductStoresQuery,
  useUpdateProductStoreMutation,
} from 'src/services/api/productStoreApi';
import { RootState } from 'src/store';
import { UrlUtils } from 'src/utils';

const selector = createSelector(
  [
    (state: RootState) => state.clients.activeClients,
    (state: RootState) => state.settings.billingSettings,
    (state: RootState) =>
      state.payments.accountInfo?.default_currency || GetDefaultCurrency(),
  ],
  (clients, billingSettings, defaultCurrency) => ({
    clients,
    billingSettings,
    defaultCurrency,
  }),
);

export const ProductStorePage = () => {
  const { EnableNewAppLayout } = useFeatureFlags();
  const drawerWidth = useDrawerWidth();
  const { billingSettings, defaultCurrency } = useAppSelector(selector);
  const { data: productStores = [], isLoading: productStoreLoading } =
    useGetProductStoresQuery();
  const [createProductStore, { isLoading: isCreateStoreLoading }] =
    useCreateProductStoreMutation();
  const [updateProductStore, { isLoading: isUpdatingProductStoreLoading }] =
    useUpdateProductStoreMutation();
  const { data: portalPrices = [], isLoading: pricesLoading } =
    useGetPricesByPortalQuery();
  const { basePortalDomain } = useCustomDomains();
  const { data: products = [] } = useGetProductsQuery();
  const { routeAccess, isLoading: isAccessLoading } = useRouteAccess();
  const productStore = productStores.at(0);

  const dispatch = useAppDispatch();

  const storeURL = UrlUtils.GetFullUrl(`${basePortalDomain}/store`);
  const handleCopy = () => {
    // Execute clipboard operation synchronously with user activation
    copy(storeURL)
      .then(() => {
        notify({
          status: 'success',
          successMessage: 'Product store link copied',
          dispatch,
        });
      })
      .catch((error) => {
        // Handle any clipboard errors
        console.error('Failed to copy:', error);
        notify({
          status: 'error',
          errorMessage: 'Failed to copy link',
          dispatch,
        });
      });
  };

  const handleSubmit = async (data: ProductStoreSchema, isPublish: boolean) => {
    // We render line items to show the UI but if they remain empty
    // We don't force user to fill them and allow saving.
    // However, we wanna make sure we don't push them to DB.
    const filteredSubscriptions = data.subscriptions.filter(
      (sub) => Boolean(sub.productId) && Boolean(sub.priceId),
    );
    const filteredOneTimeProducts = data.oneTimeProducts.filter(
      (prod) => Boolean(prod.productId) && Boolean(prod.priceId),
    );
    if (productStore) {
      const { id } = productStore;
      await updateProductStore({
        ...data,
        id,
        subscriptions: filteredSubscriptions,
        oneTimeProducts: filteredOneTimeProducts,
        isPublish,
        storeURL,
      });
    } else {
      await createProductStore({
        ...data,
        subscriptions: filteredSubscriptions,
        oneTimeProducts: filteredOneTimeProducts,
        isPublish,
        storeURL,
      });
    }
  };

  const pageContent = (
    <LoadingWrapper
      isLoading={productStoreLoading || pricesLoading || isAccessLoading}
      hideContentWhileLoading
    >
      <ProductStoreForm
        productStores={productStores}
        defaultCurrency={defaultCurrency}
        billingSettings={billingSettings}
        portalPrices={portalPrices}
        products={products}
        onSubmit={handleSubmit}
        isProductStoreLoading={
          isCreateStoreLoading || isUpdatingProductStoreLoading
        }
        onCopyDomainUrl={handleCopy}
        basePortalDomain={storeURL}
      />
    </LoadingWrapper>
  );

  if (EnableNewAppLayout) {
    return (
      <PageShell access="allowProductStore" showSpace={false}>
        <div className="w-full h-full [&>div]:overflow-hidden">
          {pageContent}
        </div>
      </PageShell>
    );
  }

  return (
    <Layout>
      <div className="w-full h-full [&>div]:overflow-hidden">
        <PageContent
          showToolbar={false}
          drawerWidth={drawerWidth}
          allowed={routeAccess.allowProductStore}
          isAccessLoading={isAccessLoading}
          showSpace={false}
        >
          {pageContent}
        </PageContent>
      </div>
    </Layout>
  );
};
